//@author: devin
import { type Type } from "@angular/core";
import { CalendarDayModal } from "../../standalone/calendar/calendar.day.modal.component";
import { type ModalComponent } from "./modal.component";
import { type ModalData } from "./modal.service";
import { ModalEventSummaryComponent } from "../../standalone/s25-event-summary/modal.event.summary.component";
import { ModalDialogComponent } from "./modals/modal.dialog.component";
import { ModalImageUploadComponent } from "./modals/modal.image.upload.component";
import { ModalCancelRequestComponent } from "./modals/modal.cancel.request.component";
import { ModalCabinetSelectComponent } from "./modals/modal.cabinet.select.component";
import { ModalDraftConflictsComponent } from "./modals/modal.draft.conflicts.component";
import { ModalRegisterEventComponent } from "./modals/modal.register.event.component";
import { ModalEditRuleComponent } from "./modals/modal.edit.rule.component";
import { ModalCreateObjectComponent } from "./modals/modal.create.object.component";
import { ModalEditEventFormConfigComponent } from "./modals/modal.edit.event.form.config.component";
import { ModalEditEmailTemplateComponent } from "./modals/modal.edit.email.template.component";
import { ModalEmailTemplatePreviewComponent } from "./modals/modal.email.template.preview.component";
import { ModalEmailScenarioComponent } from "./modals/modal.email.scenario.component";
import { ModalEmailScenarioPreviewComponent } from "./modals/modal.email.scenario.preview.component";
import { ModalEditEmbeddedConfigComponent } from "./modals/modal.edit.embedded.config.component";
import { ModalEditTaxScheduleComponent } from "../s25-pricing/modal.edit.tax.schedule.component";
import { ModalInactiveTaskComponent } from "./modals/modal.inactive.task.component";
import { ModalCreateEditTaskComponent } from "../s25-task/modal.create.edit.task.component";
import { ModalEventFormMappingComponent } from "./modals/modal.event.form.mapping.component";
import { ModalOptimizerEffectiveDatingNgComponent } from "./modals/modal.optimizer-effectived-dating.component";
import { ModalEditBuildingComponent } from "./modals/modal.edit.building.component";
import { ModalEditMasqueradeComponent } from "./modals/modal.edit.masquerade.component";
import { ModalSwitchRoleComponent } from "./modals/modal.switch.roles.component";
import { ModalEventFormPreviewComponent } from "./modals/modal.event.form.preview.component";
import { ModalIngestionFormPreviewComponent } from "./modals/modal.ingestion.form.preview.component";
import { ModalIngestionFormQrComponent } from "./modals/modal.ingestion.form.qr.component";
import { ModalAlertComponent } from "./modals/modal.alert.component";
import { ModalUserTimezonesComponent } from "./modals/modal.user.timezones.component";
import { ConfirmCancelRegistrationModalComponent } from "../student-engagement/attendance/se.confirm.cancel.registration.modal.component";
import { LoginModalComponent } from "./modals/login.modal.component";
import { SeRegisterOccurrencesModal } from "../student-engagement/attendance/se.register.occurrences.modal.component";
import { WorkflowAddFormActionsModalComponent } from "../workflow/modal/workflow.add.form.actions.modal.component";
import { SeGuestRegistrationRequestModalComponent } from "../student-engagement/attendance/se.guest.registration.request.modal.component";
import { OrganizationCreationModalComponent } from "./modals/organization.creation.modal.component";
import { ModalFreshbooksAccountComponent } from "./modals/modal.freshbooks.account.component";

export type ModalName = keyof typeof ModalMap.typedMap;
/**
 * The type of data for each modal, excluding the common ModalData properties
 * e.g. ModalDataTypes["create-object"] is `ModalCreateObjectModel`
 * @see ModalCreateObjectComponent
 */
export type ModalDataTypes = {
    [key in ModalName]: Omit<InstanceType<(typeof ModalMap.typedMap)[key]["controller"]>["data"], keyof ModalData>;
};

export type ModalDefinition = {
    size: "sm" | "md" | "lg" | "xl" | "xxl";
    title?: string;
    iconClass?: string;
    asDialog?: boolean;
    windowClass?: string;
    controller: Type<ModalComponent>;
};

export class ModalMap {
    public static typedMap = {
        dialog: { title: "Dialog", size: "md", controller: ModalDialogComponent },
        message: { title: "Message", size: "lg", controller: ModalDialogComponent },
        alert: {
            title: "Alert",
            size: "lg",
            asDialog: true,
            controller: ModalAlertComponent,
        },
        "calendar-day": {
            size: "md",
            title: "Event List",
            controller: CalendarDayModal,
        },
        "event-summary": {
            title: "Event Summary",
            size: "lg",
            asDialog: false,
            controller: ModalEventSummaryComponent,
        },
        "create-edit-task": {
            size: "lg",
            controller: ModalCreateEditTaskComponent,
            asDialog: true,
            iconClass: "ngTodo",
        },
        "create-object": {
            size: "md",
            controller: ModalCreateObjectComponent,
            asDialog: true,
        },
        "register-event": {
            size: "lg",
            title: "Register",
            controller: ModalRegisterEventComponent,
            iconClass: "ngTodo",
            asDialog: true,
        },
        "cabinet-select": {
            size: "sm",
            controller: ModalCabinetSelectComponent,
            title: "Choose Event Heading",
            asDialog: true,
            iconClass: "ngEventIcon",
        },
        "draft-conflicts": {
            size: "md",
            controller: ModalDraftConflictsComponent,
            title: "Draft Conflicts",
            asDialog: true,
            iconClass: "ngDraft",
        },
        "image-upload": {
            size: "lg",
            title: "Image Upload",
            controller: ModalImageUploadComponent,
            iconClass: "ng25Live",
            asDialog: true,
        },
        "event-form-mapping": {
            size: "md",
            controller: ModalEventFormMappingComponent,
            title: "Occurrence Mapping",
            asDialog: true,
        },
        "event-form-preview": {
            size: "md",
            title: "Preview",
            controller: ModalEventFormPreviewComponent,
            asDialog: true,
        },
        "ingestion-form-preview": {
            size: "xl",
            title: "Preview",
            controller: ModalIngestionFormPreviewComponent,
            asDialog: true,
        },
        "ingestion-form-qr": {
            size: "md",
            title: "Download QR Code",
            controller: ModalIngestionFormQrComponent,
            asDialog: true,
        },
        "cancel-request": {
            size: "md",
            title: "Event Cancellation Request",
            controller: ModalCancelRequestComponent,
            iconClass: "ng25Live",
        },
        "edit-rule": { size: "xl", title: "Edit Rule", asDialog: true, controller: ModalEditRuleComponent },
        "edit-event-form-config": {
            size: "xl",
            title: "Edit Event Form Config",
            asDialog: true,
            controller: ModalEditEventFormConfigComponent,
        },
        "edit-email-template": {
            size: "xl",
            title: "Edit Email Template",
            asDialog: true,
            controller: ModalEditEmailTemplateComponent,
        },
        "email-template-preview": {
            size: "lg",
            title: "Preview Template",
            asDialog: true,
            controller: ModalEmailTemplatePreviewComponent,
        },
        "todo-email-template-preview": {
            size: "lg",
            title: "Preview To Do",
            asDialog: true,
            controller: ModalEmailTemplatePreviewComponent,
        },
        "edit-email-scenario": {
            size: "xl",
            title: "Edit Email Scenario",
            asDialog: true,
            controller: ModalEmailScenarioComponent,
        },
        "email-scenario-preview": {
            size: "lg",
            title: "Preview Scenario",
            asDialog: true,
            controller: ModalEmailScenarioPreviewComponent,
        },
        "edit-embedded-config": {
            size: "xl",
            title: "Edit Embed Config",
            asDialog: true,
            controller: ModalEditEmbeddedConfigComponent,
        },
        "edit-tax-schedule": {
            size: "xl",
            title: "Edit Tax Schedule",
            asDialog: true,
            controller: ModalEditTaxScheduleComponent,
        },
        "inactive-task": {
            size: "lg",
            controller: ModalInactiveTaskComponent,
            asDialog: true,
        },
        "optimizer-effective-dating-ng": {
            size: "xl",
            title: "Optimizer Effective Dating",
            asDialog: true,
            controller: ModalOptimizerEffectiveDatingNgComponent,
        },
        "edit-building": {
            size: "xl",
            title: "Edit Building",
            asDialog: true,
            controller: ModalEditBuildingComponent,
        },
        "edit-masquerade": {
            size: "xl",
            title: "Edit Masquerade Mapping",
            asDialog: true,
            controller: ModalEditMasqueradeComponent,
        },
        "switch-roles": {
            size: "xl",
            title: "Switch Roles",
            asDialog: true,
            controller: ModalSwitchRoleComponent,
        },
        "user-timezones": {
            size: "md",
            title: "User Time Zone Settings",
            asDialog: true,
            controller: ModalUserTimezonesComponent,
        },
        "confirm-cancel-registrations": {
            size: "md",
            title: "Cancel Registrations",
            asDialog: true,
            controller: ConfirmCancelRegistrationModalComponent,
        },
        login: {
            size: "md",
            title: "Sign In",
            asDialog: true,
            controller: LoginModalComponent,
        },
        "se-register-occurrences": {
            size: "md",
            title: "Which occurrences do you wish to register for?",
            asDialog: true,
            controller: SeRegisterOccurrencesModal,
        },
        "workflow-add-form-actions-modal": {
            size: "xxl",
            title: "Add workflow actions for this form",
            controller: WorkflowAddFormActionsModalComponent,
        },
        "se-guest-registration-request": {
            size: "md",
            title: "Register for event",
            asDialog: true,
            controller: SeGuestRegistrationRequestModalComponent,
        },
        "organization-creation": {
            size: "md",
            title: "Organization Creation",
            asDialog: true,
            controller: OrganizationCreationModalComponent,
        },
        "freshbooks-account": {
            size: "md",
            title: "Freshbooks Account",
            asDialog: true,
            controller: ModalFreshbooksAccountComponent,
        },
    } as const;

    public static map = ModalMap.typedMap as {
        [key: string]: ModalDefinition;
    };
}
